/**
 * usePlanAnalysis - Hook for comprehensive meal analysis including nutrition and grocery calculation
 *
 * This hook provides both nutritional analysis and grocery list generation for meals,
 * working with both PRO editor plans and consumer dashboard meals.
 *
 * Features:
 * - Calculate nutrients for a set of meals
 * - Generate grocery lists with proper packaging
 * - Support for async asset fetching
 * - Envelope comparison (min/max targets)
 * - Caching and memoization
 * - Works with both virtual and saved meals
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import AuthStore from '@stores/AuthStore';
import { getConfig } from '@utils/Env';
import { getAssetsForMeals } from '@utils/Meals';
import { getGroceriesForMeals, updateGroceryPackaging } from '@utils/Grocery';
import { analyzePlan } from '@utils/Plans';

/**
 * Configuration for meal analysis
 * @typedef {Object} MealAnalysisConfig
 * @property {Array} meals - Array of meals to analyze
 * @property {Object} assets - Pre-fetched assets (recipes, foods, etc.)
 * @property {number} defaultPortion - Default portion size
 * @property {string} units - Unit system for groceries ('metric' or 'english')
 * @property {Object} profile - User profile with preferences
 * @property {Object} plan - Plan object for backend analysis
 * @property {Array} mealTypes - Meal type configurations with envelopes
 */

/**
 * Hook for comprehensive meal analysis (nutrition + groceries)
 *
 * @param {MealAnalysisConfig} config - Configuration object
 * @returns {Object} Meal analysis results and utilities
 */
export const usePlanAnalysis = (config = {}) => {
    const {
        meals = [],
        assets: providedAssets = null,
        defaultPortion = 1,
        units = 'metric',
        profile = null,
        plan = null, // Plan object needed for backend analysis
        mealTypes = [], // Meal type configurations with envelopes
        nutritionKey = 0 // Increment to force asset re-fetch after edits
    } = config;

    // State for async operations
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const prevNutritionKeyRef = useRef(nutritionKey);
    const [assets, setAssets] = useState(providedAssets || {
        recipes: {},
        foods: {},
        details: {},
        merchants: {},
        brands: {},
        locations: {}
    });

    /**
     * Check if we have any meals that need assets
     */
    const needsAssets = useCallback((meals) => {
        if (meals.length === 0) return false;

        // Simply check if we have any non-deleted meals
        return meals.some(meal => !meal.deleted);
    }, []);

    /**
     * Fetch assets for meals
     */
    const fetchMealAssets = useCallback(async (meals) => {
        if (!needsAssets(meals)) {
            return assets;
        }

        try {
            setLoading(true);
            setError(null);

            // Let getAssetsForMeals handle all the caching and deduplication
            const fetchedAssets = await getAssetsForMeals(meals);

            return fetchedAssets;
        } catch (err) {
            console.error('Failed to fetch meal assets:', err);
            setError(err);
            // Return empty assets on error instead of stale data
            return { recipes: {}, foods: {}, details: {}, brands: {}, merchants: {} };
        } finally {
            setLoading(false);
        }
    }, [assets, needsAssets]);

    /**
     * Update assets when meals change
     */
    useEffect(() => {
        if (meals.length === 0) return;

        const keyChanged = prevNutritionKeyRef.current !== nutritionKey && nutritionKey > 0;
        prevNutritionKeyRef.current = nutritionKey;

        // After a food/recipe edit, always re-fetch from the (now-fresh) cache
        if (keyChanged) {
            fetchMealAssets(meals).then(newAssets => setAssets(newAssets));
            return;
        }

        // If we have provided assets with actual content, use them
        if (providedAssets && (
            Object.keys(providedAssets.recipes || {}).length > 0 ||
            Object.keys(providedAssets.foods || {}).length > 0
        )) {
            setAssets(providedAssets);
            return;
        }

        // Otherwise fetch assets
        fetchMealAssets(meals).then(newAssets => {
            setAssets(newAssets);
        });
    }, [meals, providedAssets, nutritionKey]);

    /**
     * Re-fetch assets from cache (used after food/recipe edits update _cache.documents)
     */
    const refreshAssets = useCallback(async () => {
        if (meals.length === 0) return;
        const newAssets = await fetchMealAssets(meals);
        setAssets(newAssets);
    }, [fetchMealAssets, meals]);

    /**
     * State for backend analysis results
     */
    const [analysis, setAnalysis] = useState(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const abortControllerRef = useRef(null);

    /**
     * Analyze meals using backend endpoint
     */
    const analyzeWithBackend = useCallback(async () => {
        // Use plan.items if available, otherwise use meals
        const items = plan?.items || meals;

        if (!items?.length || !profile?.preferences) {
            setAnalysis(null);
            return;
        }

        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setAnalysisLoading(true);
        setAnalysisError(null);

        try {
            // Pass preferences verbatim - backend expects the raw data
            const response = await AuthStore.fetch(
                getConfig('users_api') + '/analyze-plan',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json; schema=plan/analysis/1'
                    },
                    signal: abortController.signal,
                    body: JSON.stringify({
                        items: items,
                        meal_types: plan?.meal_types || mealTypes || [],
                        profile: {
                            preferences: profile.preferences,
                            portion: profile.portion || 1,
                            family: profile.family || []
                        }
                    })
                }
            );

            setAnalysis(response);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setAnalysisError(err);
            }
        } finally {
            setAnalysisLoading(false);

            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
        }
    }, [plan, meals, profile]);

    /**
     * Debounced version for meal changes
     */
    const debouncedAnalyzeWithBackend = useMemo(
        () => debounce(analyzeWithBackend, 300),
        [analyzeWithBackend]
    );

    /**
     * Clean up on unmount
     */
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    /**
     * Trigger backend analysis when plan/meals or profile changes
     */
    useEffect(() => {
        // Use plan.items if available, otherwise use meals directly
        const hasItems = (plan?.items?.length > 0) || (meals?.length > 0);
        if (hasItems && profile?.preferences) {
            debouncedAnalyzeWithBackend();
        }
    }, [plan?.items, meals, profile, debouncedAnalyzeWithBackend]);

    /**
     * Calculate grocery list for meals
     */
    const groceries = useMemo(() => {
        if (meals.length === 0) {
            return [];
        }

        // Filter out deleted meals
        const activeMeals = meals.filter(meal => !meal.deleted);

        if (activeMeals.length === 0) {
            return [];
        }

        // Get raw groceries for meals
        const rawGroceries = getGroceriesForMeals(activeMeals, assets);

        if (!rawGroceries || rawGroceries.length === 0) {
            return [];
        }

        // Determine unit system from profile or config
        const unitSystem = profile?.preferences?.units || units || 'metric';

        // Update packaging based on unit preferences
        const packagedGroceries = updateGroceryPackaging(
            rawGroceries,
            assets.foods || {},
            unitSystem
        );

        return packagedGroceries;

    }, [meals, assets, profile, units]);

    /**
     * Calculate plan analysis statistics (efficiency score, hands-on time, etc.)
     */
    const planAnalysis = useMemo(() => {
        if (!plan || !plan.uuid || meals.length === 0) {
            return {
                hands_on_time: 0,
                efficiency_score: 0
            };
        }

        // Create a temporary plan object with the current meals for analysis
        const planForAnalysis = {
            ...plan,
            items: meals
        };

        const unitsMode = profile?.preferences?.units_mode || 'imperial';
        const analyzeResult = analyzePlan(planForAnalysis, assets, unitsMode);

        // analyzePlan returns { plan, assets, groceries } - we need the plan property
        const analyzedPlan = analyzeResult.plan;

        return {
            hands_on_time: analyzedPlan.hands_on_time || 0,
            efficiency_score: analyzedPlan.efficiency_score || 0,
            total_fresh: analyzedPlan.total_fresh,
            total_pantry: analyzedPlan.total_pantry,
            total_time: analyzedPlan.total_time,
            avg_cost_per_serving: analyzedPlan.avg_cost_per_serving,
        };
    }, [plan, meals, assets, profile?.preferences?.units_mode]);

    // Create indexed violations structures
    const violationsByDay = useMemo(() => {
        const byDay = {};
        Object.values(analysis?.compliance?.violations || {}).forEach(violation => {
            // Only include day-level violations
            if (violation.type === 'daily_maximum_exceeded' || violation.type === 'daily_minimum_not_met') {
                if (!byDay[violation.day]) {
                    byDay[violation.day] = [];
                }
                byDay[violation.day].push(violation);
            }
        });
        return byDay;
    }, [analysis?.compliance?.violations]);

    const violationsByMeal = useMemo(() => {
        const byMeal = {};
        Object.values(analysis?.compliance?.violations || {}).forEach(violation => {
            // Only include meal-level violations
            if (violation.type === 'meal_maximum_exceeded' || violation.type === 'meal_minimum_not_met') {
                const day = violation.day;
                const mealType = violation.meal_type; // Assuming this field exists for meal violations

                if (!byMeal[day]) {
                    byMeal[day] = {};
                }
                if (!byMeal[day][mealType]) {
                    byMeal[day][mealType] = [];
                }
                byMeal[day][mealType].push(violation);
            }
        });
        return byMeal;
    }, [analysis?.compliance?.violations]);

    return {
        // Direct properties (for test compatibility)
        groceries,

        // Analysis object (matches legacy Editor structure)
        analysis: {
            nutrients: {},
            ...(analysis || {}),

            // Indexed violations
            violationsByDay,
            violationsByMeal,

            // Plan statistics from analyzePlan
            hands_on_time: planAnalysis.hands_on_time,
            efficiency_score: planAnalysis.efficiency_score,
            total_fresh: planAnalysis.total_fresh,
            total_pantry: planAnalysis.total_pantry,
            total_time: planAnalysis.total_time,
            avg_cost_per_serving: planAnalysis.avg_cost_per_serving
        },

        // Status
        loading: loading || analysisLoading,
        error: error || analysisError,

        // Additional data
        assets,

        // Methods for refreshing after edits
        refreshAssets,
        analyzeWithBackend
    };
};

